import { useRef, useCallback } from "react";
import { useDrag } from "@use-gesture/react";
import { useSpring, config } from "@react-spring/web";

const SWIPE_DISTANCE_THRESHOLD = 50;
const SWIPE_VELOCITY_THRESHOLD = 0.5;
const EXIT_OFFSET = 400;
const OPACITY_DAMPEN = 600;

const FORM_INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

interface SwipeNavigationOptions {
  onNext: () => void;
  onPrev: () => void;
  enabled?: boolean;
  canGoNext?: boolean;
  canGoPrev?: boolean;
}

export function useSwipeNavigation({
  onNext,
  onPrev,
  enabled = true,
  canGoNext = true,
  canGoPrev = true,
}: SwipeNavigationOptions) {
  const animating = useRef(false);

  const [style, api] = useSpring(() => ({
    x: 0,
    opacity: 1,
    config: config.stiff,
  }));

  const animateEntry = useCallback(
    (fromDirection: number) => {
      api.start({ x: fromDirection * EXIT_OFFSET, opacity: 0, immediate: true });
      api.start({ x: 0, opacity: 1, onRest: () => { animating.current = false; } });
    },
    [api],
  );

  const bind = useDrag(
    ({ active, movement: [mx], velocity: [vx], direction: [dx], cancel, event }) => {
      if (animating.current) {
        cancel();
        return;
      }

      const tag = (event?.target as HTMLElement)?.tagName;
      if (tag && FORM_INPUT_TAGS.has(tag)) {
        cancel();
        return;
      }

      if ((event?.target as HTMLElement)?.closest?.("[contenteditable]")) {
        cancel();
        return;
      }

      if (!canGoPrev && mx > 0 && active) {
        api.start({ x: 0, opacity: 1, immediate: true });
        return;
      }
      if (!canGoNext && mx < 0 && active) {
        api.start({ x: 0, opacity: 1, immediate: true });
        return;
      }

      if (active) {
        api.start({
          x: mx,
          opacity: 1 - Math.abs(mx) / OPACITY_DAMPEN,
          immediate: true,
        });
        return;
      }

      const triggered = Math.abs(mx) > SWIPE_DISTANCE_THRESHOLD || vx > SWIPE_VELOCITY_THRESHOLD;
      if (!triggered) {
        api.start({ x: 0, opacity: 1 });
        return;
      }

      const swipedRight = dx > 0;
      if (swipedRight && !canGoPrev) {
        api.start({ x: 0, opacity: 1 });
        return;
      }
      if (!swipedRight && !canGoNext) {
        api.start({ x: 0, opacity: 1 });
        return;
      }

      animating.current = true;
      const exitX = swipedRight ? EXIT_OFFSET : -EXIT_OFFSET;

      api.start({
        x: exitX,
        opacity: 0,
        onRest: () => {
          if (swipedRight) {
            onPrev();
          } else {
            onNext();
          }
          animateEntry(swipedRight ? -1 : 1);
        },
      });
    },
    { axis: "x", filterTaps: true, enabled },
  );

  return { bind, style };
}
